// BookingAudit.tsx
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import AuditHistory from "./AuditHistory";
import { AuditEntry, AuditAction, UserRole } from "./types/AuditTypes";
import apiService from "../../../../../service/Api";

interface BookingAuditProps {
  bookingId: string;
}

interface BookingHistoryEntry {
  pgh_id: number;
  pgh_created_at: string;
  pgh_label: "insert" | "update" | "delete";
  pgh_obj_id: string;
  pgh_diff?: Record<string, [unknown, unknown]>;
  pgh_context?: {
    user?: number;
    url?: string;
    remote_addr?: string | null;
  };
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: BookingHistoryEntry[];
}

const LABEL_TO_ACTION: Record<BookingHistoryEntry["pgh_label"], AuditAction> = {
  insert: "created",
  update: "updated",
  delete: "deleted",
};

const IGNORED_FIELDS = new Set(["updated_at", "last_updated_by_id"]);

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  completeness_score: "completeness score",
  chema_fund_enabled: "chema fundraiser",
  reference_number: "reference number",
  package_item_id: "package",
  sales_rep_id: "sales rep",
  contact_id: "contact",
  funeral_home_id: "funeral home",
  booking_type: "booking type",
  source_of_booking: "source of booking",
  payment_status: "payment status",
  account_status: "account status",
  go_fund_me_enabled: "GoFundMe",
  agreed_to_terms: "terms agreement",
  confirmed_authority: "confirmed authority",
};

function humanizeField(field: string): string {
  return (
    FIELD_LABEL_OVERRIDES[field] ??
    field.replace(/_id$/, "").replace(/_/g, " ")
  );
}

type UserLookup = {
  id: number;
  first_name?: string;
  last_name?: string;
  role?: string;
};

async function fetchUsersByIds(ids: number[]): Promise<UserLookup[]> {
  const results = await Promise.all(
    ids.map(async (id) => {
      try {
        const res = await apiService.get<UserLookup>(
          `/api/authentication/users/${id}/`,
        );
        return res.data;
      } catch (err) {
        console.error(`Failed to fetch user ${id} for audit history:`, err);
        return null;
      }
    }),
  );
  return results.filter((u): u is UserLookup => u !== null);
}

async function fetchAllPages(
  bookingId: string,
  label: string,
): Promise<BookingHistoryEntry[]> {
  const all: BookingHistoryEntry[] = [];
  let url: string | null =
    `api/bookings/booking/${bookingId}/history/?label=${label}`;

  while (url) {
    const res = (await apiService.get(url)) as { data: PaginatedResponse };
    all.push(...res.data.results);
    url = res.data.next
      ? res.data.next.replace(/^https?:\/\/[^/]+\//, "")
      : null;
  }

  return all;
}

export default function BookingAudit({ bookingId }: BookingAuditProps) {
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bookingId) return;

    async function fetchHistory() {
      setLoading(true);
      setError(null);

      try {
        const [inserts, updates, deletes] = await Promise.all([
          fetchAllPages(bookingId, "insert"),
          fetchAllPages(bookingId, "update"),
          fetchAllPages(bookingId, "delete"),
        ]);

        const allEntries = [...inserts, ...updates, ...deletes];

        allEntries.sort(
          (a, b) =>
            new Date(b.pgh_created_at).getTime() -
            new Date(a.pgh_created_at).getTime(),
        );
        const uniqueUserIds = Array.from(
          new Set(
            allEntries
              .map((entry) => entry.pgh_context?.user)
              .filter((id): id is number => typeof id === "number"),
          ),
        );
        const users = await fetchUsersByIds(uniqueUserIds);

        const mapped: AuditEntry[] = allEntries.map((entry) => {
          const userId = entry.pgh_context?.user;
          const user = users.find((u) => u.id === userId);

          const userName = user
            ? [user.first_name, user.last_name ? `${user.last_name.charAt(0)}.` : ""]
                .filter(Boolean)
                .join(" ") || "System"
            : "System";

          const userRole: UserRole = user ? (user.role as UserRole) : "System";

          const description = buildChangeDescription(
            entry.pgh_label,
            entry.pgh_diff,
            users,
          );

          return {
            id: String(entry.pgh_id),
            action: LABEL_TO_ACTION[entry.pgh_label],
            userName,
            userRole,
            description,
            timestamp: new Date(entry.pgh_created_at),
          } satisfies AuditEntry;
        });

        setAuditEntries(mapped);
      } catch (err) {
        console.error("Failed to fetch booking history:", err);
        setError("Could not load audit history. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    fetchHistory();
  }, [bookingId]);

  if (loading) return <p className="text-sm text-gray-500">Loading history…</p>;
  if (error) return <p className="text-sm text-red-500">{error}</p>;

  return <AuditHistory entries={auditEntries} />;
}

function buildChangeDescription(
  label: BookingHistoryEntry["pgh_label"],
  diff?: Record<string, [unknown, unknown]>,
  users: UserLookup[] = [],
): ReactNode {
  if (label === "insert") return "Created this booking";
  if (label === "delete") return "Deleted this booking";

  if (diff) {
    const meaningfulFields = Object.keys(diff).filter(
      (f) => !IGNORED_FIELDS.has(f),
    );

    if (meaningfulFields.length === 0) return "Updated this booking";

    return (
      <>
        Updated{" "}
        {meaningfulFields.map((field, index) => {
          const [oldVal, newVal] = diff[field];
          const fieldLabel = humanizeField(field);
          const isUserField = field.endsWith("_id") || field === "user";

          return (
            <span key={field}>
              {index > 0 && ", "}
              {fieldLabel}: <strong>{formatValue(oldVal, isUserField, users)}</strong> to{" "}
              <strong>{formatValue(newVal, isUserField, users)}</strong>
            </span>
          );
        })}
      </>
    );
  }

  return "Updated this booking";
}

function formatValue(
  val: unknown,
  isUserField = false,
  users: UserLookup[] = [],
): string {
  if (val === null || val === undefined) return "—";

  if (isUserField && typeof val === "number") {
    const user = users.find((u) => u.id === val);
    return user
      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
      : String(val);
  }

  if (typeof val === "string") {
    const isoMatch = val.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    if (isoMatch) return isoMatch[1].replace("T", " ");
    return val;
  }

  return String(val);
}
